import Phaser from 'phaser';
import { gameConfig } from './config';
import { isMobileDevice, requestFullscreen } from './utils/MobileHelper';
import { isMobileOrTablet, applyMobileOptimizations } from './utils/DeviceUtils';
import { VisibilityManager } from './systems/VisibilityManager';
import { addBugReportButton, runContextLine } from './ui/BugReportButton';
import { addFeedbackButton } from './ui/FeedbackButton';
import { LogCapture } from './systems/LogCapture';
import { CrashHandler } from './systems/CrashHandler';
import { showCrashOverlay, isCrashOverlayVisible } from './ui/CrashOverlay';
import { LifetimeStats } from './systems/LifetimeStats';

// === CAPTURE DE LOGS GLOBALE ===
// Doit être init AVANT la création du jeu pour intercepter les premiers logs.
LogCapture.init();

// === POINT D'ENTRÉE DU JEU ===
const game = new Phaser.Game(gameConfig);

// === BOUTON BUG REPORT GLOBAL ===
// S'injecte automatiquement dans toutes les scènes au moment de leur create(),
// sauf celles explicitement exclues (DevScene = debug interne).
const BUG_BUTTON_EXCLUDED = new Set(['DevScene']);
game.events.once('ready', () => {
  for (const scene of game.scene.scenes) {
    if (BUG_BUTTON_EXCLUDED.has(scene.scene.key)) continue;
    scene.events.on('create', () => {
      const run = (scene as { run?: unknown }).run;
      const ctxProvider = run ? () => runContextLine(run as Parameters<typeof runContextLine>[0]) : undefined;
      addBugReportButton(scene, ctxProvider);
      // Bouton feedback à gauche du bouton bug — même cycle de vie
      addFeedbackButton(scene);
    });
  }
});

// Debug console navigateur
(window as any).__GAME__ = game;

// === GESTION VISIBILITÉ (pause/reprise sur changement d'onglet) ===
VisibilityManager.init(game);

// === OPTIMISATIONS PERFORMANCE MOBILE ===
if (isMobileOrTablet()) {
  applyMobileOptimizations(game);
}

// === MOBILE : plein écran au premier tap (portrait, pas de lock paysage) ===
if (isMobileDevice()) {
  const onFirstTouch = () => {
    requestFullscreen();
    document.removeEventListener('touchstart', onFirstTouch);
  };
  document.addEventListener('touchstart', onFirstTouch, { once: true });

  // Refresh du canvas après changement d'orientation
  window.addEventListener('orientationchange', () => {
    setTimeout(() => {
      game.scale.refresh();
    }, 300);
  });
}

// === CRASH RECOVERY ===
// Capture les erreurs JS non gérées (sync + Promise rejections) et affiche
// un overlay clair : retour menu, signaler le crash (bug report pré-rempli),
// copier les détails. Évite les redirections silencieuses qui masquaient les bugs.
CrashHandler.install({
  onCrash: (info) => {
    // Si l'overlay est déjà visible, le 1er crash garde la main.
    if (isCrashOverlayVisible()) return;

    // Identifier la scène active (la 1ère qui n'est pas DevScene).
    let activeKey = 'BootScene';
    let contextLine: string | undefined;
    try {
      const active = game.scene.getScenes(true);
      const visible = active.find(s => s.scene.key !== 'DevScene') ?? active[0];
      if (visible) {
        activeKey = visible.scene.key;
        const run = (visible as { run?: unknown }).run;
        if (run) {
          try { contextLine = runContextLine(run as Parameters<typeof runContextLine>[0]); }
          catch { /* run incomplet, on n'inclut pas le contexte */ }
        }
      }
    } catch { /* game.scene indisponible — on retombe sur les valeurs par défaut */ }

    showCrashOverlay(info, {
      currentScene: activeKey,
      canvas: game.canvas,
      contextLine,
      onReturnToMenu: () => {
        // Best-effort : couper toutes les scènes actives puis relancer Boot.
        // Si une scène pète encore au stop, on tente quand même Boot.
        try {
          for (const s of game.scene.getScenes(true)) {
            try { game.scene.stop(s.scene.key); } catch { /* ignore */ }
          }
        } catch { /* ignore */ }
        try { game.scene.start('BootScene'); } catch { /* ignore */ }
      },
    });
  },
});

// === HEARTBEAT LIFETIME STATS ===
// Toutes les secondes, on incrémente le compteur de temps de jeu cumulé
// tant que l'onglet est visible. Précis à ~1s près, suffisant pour une
// stat agrégée de fin de partie. Pause auto quand l'onglet passe en
// arrière-plan (visibilitychange) — pas besoin de gérer ça nous-mêmes.
const PLAY_TICK_MS = 1000;
setInterval(() => {
  try {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    LifetimeStats.tick(PLAY_TICK_MS);
  } catch { /* localStorage indispo — on ignore */ }
}, PLAY_TICK_MS);

// === RACCOURCI F1 : OUVRIR LE MODE DEV ===
// Fonctionne depuis n'importe quelle scène.
window.addEventListener('keydown', (e) => {
  if (e.key === 'F1') {
    e.preventDefault();
    const devScene = game.scene.getScene('DevScene');
    if (devScene && game.scene.isActive('DevScene')) {
      game.scene.stop('DevScene');
    } else {
      // Récupérer le run de la scène active
      const activeScenes = game.scene.getScenes(true);
      const currentScene = activeScenes.find(s => s.scene.key !== 'DevScene');
      const run = (currentScene as any)?.run;
      game.scene.start('DevScene', { run });
    }
  }
});
